import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useEvent } from '../contexts/EventContext';
import { Button } from '../components/ui/button';
import { Car, Trophy, Users, Calendar, AlertCircle, LayoutDashboard } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Header component extracted to prevent re-renders
function LandingHeader({ eventName, userRole, dashboardLink }) {
  return (
    <header className="bg-white border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <img 
            src="/c4ac-logo.png" 
            alt="Cruise for a Cause" 
            className="h-16 w-auto"
          />
          <span className="text-xl font-bold text-gray-900 hidden sm:inline">
            {eventName || 'Cruise for a Cause'}
          </span>
        </div>
        <div className="flex items-center space-x-4">
          {userRole ? (
            <Link to={dashboardLink}>
              <Button className="bg-pink-600 hover:bg-pink-700 text-white">
                <LayoutDashboard className="w-4 h-4 mr-2" />
                Go to Dashboard
              </Button>
            </Link>
          ) : (
            <Link to="/login">
              <Button variant="outline" className="border-pink-500 text-pink-600 hover:bg-pink-50">
                Admin Login
              </Button>
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}

// Footer component
function LandingFooter() {
  return (
    <footer className="border-t border-gray-200 mt-20 bg-white">
      <div className="max-w-7xl mx-auto px-6 py-8 text-center">
        <p style={{ color: '#111827' }}>&copy; 2026 Cruise for a Cause. All rights reserved.</p>
        <p className="mt-2" style={{ color: '#374151' }}>
          Developed by <a href="https://greatmarketingllc.com" target="_blank" rel="noopener noreferrer" className="text-pink-600 hover:text-pink-500 font-medium">Great Marketing LLC</a>
        </p>
      </div>
    </footer>
  );
}

export default function LandingPage() {
  const { currentEvent, loading } = useEvent();
  const [userRole, setUserRole] = useState(null);

  const checkUserAuth = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setUserRole(null);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserRole(response.data.role);
    } catch (error) {
      // Token invalid or expired
      setUserRole(null);
    }
  }, []);

  useEffect(() => {
    checkUserAuth();
  }, [checkUserAuth]);

  const getDashboardLink = () => {
    if (userRole === 'admin' || userRole === 'super_admin') {
      return '/admin/dashboard';
    } else if (userRole === 'judge') {
      return '/judge/dashboard';
    }
    return '/login';
  };

  // Show loading spinner only while actually loading
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  // No active events - show landing page with message
  if (!currentEvent) {
    return (
      <div className="min-h-screen bg-gray-50">
        <LandingHeader userRole={userRole} dashboardLink={getDashboardLink()} />

        {/* No Events Message */}
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="text-center mb-16">
            <img 
              src="/c4ac-logo.png" 
              alt="Cruise for a Cause" 
              className="h-32 w-auto mx-auto mb-8"
            />
            <h1 className="text-5xl md:text-7xl font-bold mb-6 text-gray-900">
              Cruise for a Cause
            </h1>
            
            {/* No Events Notice */}
            <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-8 max-w-2xl mx-auto mb-12 shadow-md">
              <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-3 text-gray-900">No Active Events</h3>
              <p className="text-gray-700 text-lg mb-4">
                There are no car show events currently happening.
              </p>
              <p className="text-gray-600">
                Please check back soon or visit our website for upcoming event announcements!
              </p>
            </div>

            <a href="https://cruise4acause.org/events-upcoming/" target="_blank" rel="noopener noreferrer">
              <Button className="btn-c4ac text-white px-8 py-6 text-lg font-semibold">
                View Upcoming Events
              </Button>
            </a>
          </div>
        </div>

        <LandingFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <LandingHeader eventName={currentEvent.name} userRole={userRole} dashboardLink={getDashboardLink()} />

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-16">
          <img 
            src="/c4ac-logo.png" 
            alt="Cruise for a Cause" 
            className="h-32 w-auto mx-auto mb-8"
          />
          <h1 className="text-5xl md:text-7xl font-bold mb-6 text-gray-900">
            {currentEvent.name}
          </h1>
          <p className="text-xl text-gray-700 mb-8 max-w-3xl mx-auto">
            {currentEvent.description || 'Join us for an incredible car show event featuring the finest vehicles and passionate enthusiasts.'}
          </p>
          
          {currentEvent.start_date && (
            <div className="flex items-center justify-center gap-2 mb-8">
              <Calendar className="w-5 h-5 text-gray-800" />
              <span className="text-lg font-medium text-gray-800">
                {new Date(currentEvent.start_date).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  timeZone: 'UTC'
                })}
              </span>
            </div>
          )}

          <div className="bg-white border-2 border-pink-400 rounded-lg p-6 max-w-2xl mx-auto mb-12 shadow-md">
            <h3 className="text-xl font-bold mb-3 text-gray-900">How to Register Your Car</h3>
            <ol className="text-left text-gray-700 space-y-2">
              <li>1. Purchase a car show ticket from <a href="https://cruise4acause.org/events-upcoming/" target="_blank" rel="noopener noreferrer" className="text-pink-600 hover:text-pink-500 underline font-medium">cruise4acause.org</a></li>
              <li>2. Fill out your vehicle information during checkout</li>
              <li>3. Your car will be automatically registered!</li>
              <li>4. Check back here to view the gallery and leaderboard</li>
            </ol>
          </div>

          <a href="https://cruise4acause.org/events-upcoming/" target="_blank" rel="noopener noreferrer">
            <Button className="btn-c4ac text-white px-8 py-6 text-lg font-semibold">
              Purchase Ticket & Register Car
            </Button>
          </a>
        </div>

        {/* Feature Cards */}
        <div className="grid md:grid-cols-3 gap-8 mt-20">
          <Link to="/gallery">
            <div className="bg-white border border-gray-200 p-8 rounded-lg shadow-sm hover:border-pink-300 hover:shadow-md transition-all cursor-pointer group">
              <Car className="w-12 h-12 text-pink-500 mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-2xl font-bold mb-3 text-gray-900">Gallery</h3>
              <p className="text-gray-700">
                Browse all registered vehicles and see the amazing cars participating in the show.
              </p>
            </div>
          </Link>

          <Link to="/leaderboard">
            <div className="bg-white border border-gray-200 p-8 rounded-lg shadow-sm hover:border-pink-300 hover:shadow-md transition-all cursor-pointer group">
              <Trophy className="w-12 h-12 text-yellow-500 mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-2xl font-bold mb-3 text-gray-900">Leaderboard</h3>
              <p className="text-gray-700">
                See the top-ranked vehicles and current standings in the competition.
              </p>
            </div>
          </Link>

          <Link to="/vote">
            <div className="bg-white border border-gray-200 p-8 rounded-lg shadow-sm hover:border-pink-300 hover:shadow-md transition-all cursor-pointer group">
              <Users className="w-12 h-12 text-pink-500 mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-2xl font-bold mb-3 text-gray-900">Public Voting</h3>
              <p className="text-gray-700">
                Cast your vote for your favorite cars in various categories.
              </p>
            </div>
          </Link>
        </div>
      </div>

      <LandingFooter />
    </div>
  );
}
